<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";
import { sampleList } from "./sampleList";

const route = useRoute();
const mobileMenuOpen = ref(false);
const mobileMenuActive = ref(false);

const hasEmbeddedMode = (mode: unknown) => {
  if (Array.isArray(mode)) {
    return mode.includes("embed");
  }

  return mode === "embed";
};

const hasEmbeddedModeInLocation = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return new URLSearchParams(window.location.search).get("mode") === "embed";
};

const embedded = computed(() => hasEmbeddedMode(route.query.mode) || hasEmbeddedModeInLocation());

const isActiveRoute = (path: string) => {
  const isRoot = route.path === "/" && path === "basic-init";
  const isExactMatch = route.path === `/${path}`;
  return isRoot || isExactMatch;
};

const openMobileMenu = async () => {
  mobileMenuOpen.value = true;
  mobileMenuActive.value = false;
  await nextTick();
  mobileMenuActive.value = true;
};

const closeMobileMenu = () => {
  mobileMenuActive.value = false;
  mobileMenuOpen.value = false;
};

watch(
  () => route.path,
  () => {
    if (mobileMenuOpen.value) {
      closeMobileMenu();
    }
  }
);
</script>

<template>
  <div class="app-container">
    <header v-if="!embedded" class="header">
      <div class="header-logo">
        <a
          href="https://dhtmlx.com"
          title="DHTMLX - JavaScript Web App Framework & UI Widgets"
          class="dhx-logo"
          target="_blank"
          rel="noreferrer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="47" height="28" viewBox="0 0 47 28" fill="none">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M40.8915 20.4166L37.0415 13.1833L33.1915 20.4166H27.4165L33.8332 9.85828L27.4165 0H33.1915L37.0415 5.94997L40.8915 0H46.6665L39.6082 9.85828L46.6665 20.4166H40.8915ZM0 23.3332V22.1665H46.6667V23.3332H0ZM26.8333 20.4166L31.5 13.1833V6.99997L26.8333 0V7.2333H20.4167V0H15.1667V2.21666C16.1 2.97499 16.9167 3.90831 17.5 5.07498C18.2583 6.5333 18.6667 8.22496 18.6667 10.2083C18.6667 12.1333 18.2583 13.8249 17.4417 15.3416C16.8 16.4499 16.0417 17.3832 15.1667 18.1416V20.4166H20.4167V11.8416H26.8333V20.4166ZM18.0833 10.2083C18.0833 12.1333 17.675 13.8249 16.8583 15.3416C16.0417 16.8582 14.9917 18.0249 13.7083 18.7832C12.6583 19.4832 11.4917 19.8916 10.2667 20.1249C9.04167 20.2999 7.7 20.4166 6.3 20.4166H0V0H6.3C7.7 0 9.04167 0.116666 10.325 0.291665C11.55 0.466664 12.7167 0.933329 13.825 1.63333C15.1667 2.50832 16.2167 3.67498 16.975 5.13331C17.675 6.5333 18.0833 8.28329 18.0833 10.2083ZM10.2667 4.55005C11.2001 5.07505 11.9001 5.83338 12.3667 6.76671C12.8334 7.70004 13.0667 8.8667 13.1251 10.15C13.1251 11.55 12.8917 12.6583 12.4834 13.5917C12.0167 14.525 11.3751 15.2833 10.5584 15.8083C9.85839 16.275 9.10006 16.5083 8.34172 16.5667C7.52506 16.625 6.59172 16.625 5.42506 16.625H5.30839V3.85006H5.42506C6.47506 3.85006 7.35006 3.85006 8.10839 3.90839C8.86672 3.96672 9.56672 4.14172 10.2667 4.55005ZM2.33333 25.0833V25.6667H1.16667V27.4167H2.33333V28H0.583333V25.0833H2.33333ZM2.33333 27.4167V25.6667H2.91667V27.4167H2.33333ZM11.0833 26.25H9.91667V25.0833H9.33333V28H9.91667V26.8333H11.0833V28H11.6667V25.0833H11.0833V26.25ZM20.4167 25.0833V25.6667H19.25V28H18.6667V25.6667H17.5V25.0833H20.4167ZM27.4167 25.6667H26.8333V25.0833H26.25V28H26.8333V26.25H27.4167V26.8333H28V26.25H28.5833V28H29.1667V25.0833H28.5833V25.6667H28V26.25H27.4167V25.6667ZM35 25.0833H35.5833V27.4167H37.3333V28H35V25.0833ZM44.3332 25.6667H43.7499V25.0833H43.1665V25.6667H43.7499V26.25H44.3332V25.6667ZM43.7499 26.8333H44.3332V27.4167H43.7499V26.8333ZM43.7499 27.4167V28H43.1665V27.4167H43.7499ZM46.0833 25.6667V25.0833H45.5V25.6667H46.0833ZM44.9166 26.2497V25.6664H45.4999V26.2497H44.9166ZM44.9168 26.2499H44.3335V26.8332H44.9168V27.4165H45.5001V27.9998H46.0835V27.4165H45.5001V26.8332H44.9168V26.2499Z"
              fill="#2095F3"
            />
          </svg>
        </a>
        <span class="header-title mobile-only">Vue Gantt Samples</span>
      </div>

      <nav class="header-nav desktop-only">
        <a class="menu-link" href="https://docs.dhtmlx.com/gantt/integrations/vue/" target="_blank" rel="noreferrer">
          Documentation
        </a>
        <a class="menu-link" href="https://docs.dhtmlx.com/gantt/" target="_blank" rel="noreferrer">
          JS Gantt Docs
        </a>
        <a class="menu-link" href="https://forum.dhtmlx.com/c/gantt" target="_blank" rel="noreferrer">Forum</a>
      </nav>

      <button class="burger-button mobile-only" aria-label="Open menu" @click="openMobileMenu">&#9776;</button>
    </header>

    <div class="layout-body">
      <nav v-if="!embedded" class="sidebar">
        <h2 class="sidebar-title">Vue Gantt Samples</h2>
        <ul class="navmenu-ul">
          <li v-for="sample in sampleList" :key="sample.path" class="navmenu-li">
            <RouterLink
              :to="`/${sample.path}`"
              :class="['sidebar-link', { active: isActiveRoute(sample.path) }]"
            >
              {{ sample.name }}
            </RouterLink>
          </li>
        </ul>
      </nav>

      <main class="main-content">
        <RouterView />
      </main>
    </div>

    <div v-if="!embedded && mobileMenuOpen" class="mobile-menu-backdrop" @click="closeMobileMenu">
      <div :class="['mobile-menu-panel', { 'mobile-menu-panel-active': mobileMenuActive }]" @click.stop>
        <div class="mobile-button-wrapper">
          <button class="close-button" aria-label="Close menu" @click="closeMobileMenu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g id="assets/close">
                <path
                  id="Vector"
                  d="M5 6.41L6.41 5L12 10.59L17.59 5L19 6.41L13.41 12L19 17.59L17.59 19L12 13.41L6.41 19L5 17.59L10.59 12L5 6.41Z"
                  fill="#606770"
                />
              </g>
            </svg>
          </button>
        </div>

        <h2 class="sidebar-title">Vue Gantt Samples</h2>

        <ul class="navmenu-ul">
          <li v-for="sample in sampleList" :key="sample.path" class="navmenu-li">
            <RouterLink
              :to="`/${sample.path}`"
              :class="['sidebar-link', { active: isActiveRoute(sample.path) }]"
              @click="closeMobileMenu"
            >
              {{ sample.name }}
            </RouterLink>
          </li>
        </ul>

      </div>
    </div>
  </div>
</template>
